import { ChargeEventRow } from "./chargeEventRow";
import { FuelUpEventRow } from "./fuelUpEventRow";

export interface ChargeTotals {
  /** Energy taken from chargers, or `null` when no event carried a figure. */
  chargedKWh: number | null;
  /** Time spent charging, or `null` when no event carried a duration. */
  durationMs: number | null;
}

/**
 * Adds up what a vehicle took from chargers in the period.
 *
 * A total of `null` rather than `0` where nothing was reported: an EV that charged five times
 * without Geotab measuring any of them must not read as one that never plugged in.
 *
 * @param rows - The charge event rows of one vehicle.
 */
export function chargeTotals(rows: ChargeEventRow[]): ChargeTotals {
  const energies = rows.map(row => row.energyConsumedKwh).filter(isNumber);
  const durations = rows.map(row => row.durationHours).filter(isNumber);

  return {
    chargedKWh: energies.length > 0 ? sum(energies) : null,
    durationMs: durations.length > 0 ? sum(durations) * 3_600_000 : null,
  };
}

export interface FillTotals {
  /** Volume tanked, or `null` when no fill-up carried a volume. */
  filledLiters: number | null;
  /** Fill-ups that were detected but whose volume could not be established. */
  fillsWithoutVolume: number;
}

/**
 * Adds up what a vehicle tanked in the period.
 *
 * The fuel card volume is taken where there is one, being a receipt rather than an estimate from
 * the tank level. Fill-ups with neither are counted instead of ignored, because the total is then
 * short by an unknown amount and the reader has to be told.
 *
 * @param rows - The fill-up event rows of one vehicle.
 */
export function fillTotals(rows: FuelUpEventRow[]): FillTotals {
  const volumes = rows.map(row => row.cardVolumeLiters ?? row.volumeLiters).filter(isNumber);

  return {
    filledLiters: volumes.length > 0 ? sum(volumes) : null,
    fillsWithoutVolume: rows.length - volumes.length,
  };
}

function sum(values: number[]) {
  return values.reduce((total, value) => total + value, 0);
}

function isNumber(value: number | null): value is number {
  return value != null;
}
